import { useEffect, useState } from 'react';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  PROJECT_SLUG,
  useComponentPageIfAny,
} from '../component/ComponentPageContext';
import { formatStarted } from '../pipeline/format';
import type { Outcome } from '../pipeline/outcome';
import { OutcomePill } from '../pipeline/OutcomePill';
import { slugQuery, useInsights } from '../pipeline/useInsights';
import type { Loaded } from '../pipeline/useInsights';
import type { PullRequest, PullRequestsAnswer } from './types';

const PAGE_SIZE = 10;

const useStyles = makeStyles(theme => ({
  filter: { marginBottom: theme.spacing(2), minWidth: 260 },
  // Fixed layout: the columns share the card's width instead of the longest
  // title or branch setting it, so the table never runs past the card's edge.
  table: { tableLayout: 'fixed', width: '100%' },
  cell: {
    padding: theme.spacing(0.75, 1.25),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  labelsCell: { padding: theme.spacing(0.75, 1.25) },
  labels: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  label: { maxWidth: '100%' },
  number: { color: 'var(--ui-text-muted)' },
  branch: { fontFamily: 'var(--font-mono)', fontSize: '0.8rem' },
  compactList: { listStyle: 'none', margin: 0, padding: 0 },
  compactRow: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  compactTitle: { minWidth: 0, overflowWrap: 'anywhere' },
  compactMeta: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  more: { marginTop: theme.spacing(1.5) },
  source: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
}));

/**
 * A pull request's state in words, read from the same fields the Overview's
 * row counts — so a mark here and that count cannot disagree.
 */
function stateOf(pr: PullRequest): Outcome {
  if (pr.isDraft) return { kind: 'neutral', label: 'Draft' };
  if (pr.needsReview) return { kind: 'running', label: 'Needs review' };
  return { kind: 'success', label: 'Open' };
}

const labelsOf = (pr: PullRequest) =>
  pr.labels.filter((label): label is string => Boolean(label));

function branchOf(pr: PullRequest): string | undefined {
  if (!pr.headRef) return undefined;
  return pr.baseRef ? `${pr.headRef} → ${pr.baseRef}` : pr.headRef;
}

/** Whether `pr` holds every word of `query` in its number, title, branch, author or labels. */
function matches(pr: PullRequest, query: string): boolean {
  const haystack = [
    `#${pr.number}`,
    pr.title,
    pr.headRef,
    pr.baseRef,
    pr.author,
    ...labelsOf(pr),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(word => haystack.includes(word));
}

const Updated = ({ pr }: { pr: PullRequest }) => {
  const updated = formatStarted(pr.updatedAt, Date.now());
  if (!updated || !pr.updatedAt) return <>Unknown</>;
  return (
    <time dateTime={pr.updatedAt} title={updated.exact}>
      {updated.label}
    </time>
  );
};

/** An entry's second line: branches, author, labels, then the last update. */
const CompactDetails = ({
  pr,
  className,
}: {
  pr: PullRequest;
  className: string;
}) => {
  const details = [branchOf(pr), pr.author, labelsOf(pr).join(', ')]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={className}>
      {details && `${details} · `}
      <Updated pr={pr} />
    </div>
  );
};

/** The card itself, for one repository's answer. */
const PullRequestsCard = ({
  slug,
  answer,
}: {
  slug: string;
  answer: Loaded<PullRequestsAnswer>;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Below the large breakpoint the content column next to the navigation is
  // too narrow for six columns, so each pull request becomes a two-line
  // entry — the CI/CD tab's run history draws the same line. Asked on the
  // first render, so a phone never paints the table first.
  const narrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const [query, setQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  const pullsUrl = `https://github.com/${slug}/pulls`;
  const all = answer.data?.pullRequests ?? [];
  const shown = query ? all.filter(pr => matches(pr, query)) : all;
  // A list that came back shorter than the page the reader was on shows its
  // last page rather than an empty table with no way back.
  const current = Math.min(
    pageIndex,
    Math.max(0, Math.ceil(shown.length / PAGE_SIZE) - 1),
  );
  const pagePrs = shown.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  let body: JSX.Element;
  if (answer.loading) {
    body = <Progress />;
  } else if (answer.error) {
    body = (
      <Typography role="alert">
        Couldn't load the pull requests from GitHub.
      </Typography>
    );
  } else if (!all.length) {
    body = <Typography>No open pull requests for {slug}.</Typography>;
  } else {
    body = (
      <>
        <TextField
          id={`${slug}-pull-request-filter`}
          type="search"
          size="small"
          variant="outlined"
          label="Filter"
          placeholder="Title, branch, author or label"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setPageIndex(0);
          }}
          InputLabelProps={{ shrink: true }}
          className={classes.filter}
        />

        {!shown.length && (
          <Typography>
            None of the {all.length} listed pull requests match.
          </Typography>
        )}

        {shown.length > 0 && narrow && (
          <ul className={classes.compactList} aria-label="Open pull requests">
            {pagePrs.map(pr => (
              <li key={pr.number} className={classes.compactRow}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gridGap={8}
                  alignItems="center"
                >
                  <span className={classes.compactTitle}>
                    <span className={classes.number}>#{pr.number}</span>{' '}
                    <Link to={pr.htmlUrl}>{pr.title}</Link>
                  </span>
                  <OutcomePill outcome={stateOf(pr)} />
                </Box>
                <CompactDetails pr={pr} className={classes.compactMeta} />
              </li>
            ))}
          </ul>
        )}

        {shown.length > 0 && !narrow && (
          <Table
            size="small"
            aria-label="Open pull requests"
            className={classes.table}
          >
            <colgroup>
              <col style={{ width: '34%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {['Title', 'Status', 'Branch', 'Author', 'Labels'].map(
                  title => (
                    <TableCell key={title} className={classes.cell}>
                      {title}
                    </TableCell>
                  ),
                )}
                <TableCell align="right" className={classes.cell}>
                  Updated
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagePrs.map(pr => (
                <TableRow key={pr.number}>
                  <TableCell
                    className={classes.cell}
                    title={`#${pr.number} ${pr.title}`}
                  >
                    <span className={classes.number}>#{pr.number}</span>{' '}
                    <Link to={pr.htmlUrl}>{pr.title}</Link>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <OutcomePill outcome={stateOf(pr)} />
                  </TableCell>
                  <TableCell
                    className={`${classes.cell} ${classes.branch}`}
                    title={branchOf(pr)}
                  >
                    {branchOf(pr) ?? 'Unknown'}
                  </TableCell>
                  <TableCell className={classes.cell} title={pr.author}>
                    {pr.author ?? 'Unknown'}
                  </TableCell>
                  <TableCell className={classes.labelsCell}>
                    <div className={classes.labels}>
                      {labelsOf(pr).map(label => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          title={label}
                          className={classes.label}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell align="right" className={classes.cell}>
                    <Updated pr={pr} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {shown.length > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={shown.length}
            page={current}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[]}
            onPageChange={(_e, next) => setPageIndex(next)}
          />
        )}

        {/* GitHub hands out one page of them; past it, say where the rest
            are rather than letting a hundred read as all of them. */}
        {answer.data?.complete === false && (
          <Typography variant="body2" className={classes.more}>
            Showing the {all.length} most recently updated —{' '}
            <Link to={pullsUrl}>more are open on GitHub</Link>
          </Typography>
        )}
      </>
    );
  }

  return (
    <InfoCard
      title="Open pull requests"
      action={
        <Box pt={1} pr={1}>
          <Link to={pullsUrl}>Open in GitHub</Link>
        </Box>
      }
    >
      {body}
      <div className={classes.source}>Source: github-insights · {slug}</div>
    </InfoCard>
  );
};

/**
 * The Pull Requests tab of a component's page — and of an API's page, which
 * shares it (specs/component-pull-requests.md). The open pull requests of the
 * entity's repository, read server-side through the portal's GitHub App, so no
 * reader needs a GitHub login of their own.
 *
 * On a component's page the tab reads the answer the page already holds, the
 * one the tab strip and the Overview count from, and asks for it again on
 * opening so a page left open does not show an old list; an API's page has no
 * such answer, so there the tab asks for itself.
 */
export const PullRequestsContent = () => {
  const { entity } = useEntity();
  const slug = entity.metadata.annotations?.[PROJECT_SLUG];
  const page = useComponentPageIfAny();
  const own = useInsights<PullRequestsAnswer>(
    !page && slug ? `/pull-requests?${slugQuery(slug)}` : undefined,
  );

  const refresh = page?.refreshPullRequests;
  const pageIsAsking = page?.pullRequests.loading;
  useEffect(() => {
    // Once, on opening — unless the page is asking right now anyway, as it is
    // when it was opened at this tab's address.
    if (refresh && !pageIsAsking) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!slug) {
    return (
      <InfoCard title="No pull requests available for this entity">
        Add a <code>github.com/project-slug</code> annotation to this
        component's <code>catalog-info.yaml</code> to enable pull requests.
      </InfoCard>
    );
  }

  // Keyed by the repository, so another entity's list starts on its first
  // page with no filter rather than inheriting this one's.
  return (
    <PullRequestsCard
      key={slug}
      slug={slug}
      answer={page ? page.pullRequests : own}
    />
  );
};
